import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { DashboardLayout } from '@/components/layout/DashboardLayout.tsx';
import { OrganizationForm } from '@/components/forms/OrganizationForm';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useOrganization, useUpdateOrganization, useDeleteOrganization, useOrganizationContacts, useUpdateOrganizationContact, useDeleteOrganizationContact, useOrganizationEstablishments, useCreateOrganizationEstablishment, useDeleteOrganizationEstablishment } from '@/hooks';
import { ContactsList, type ContactEditData } from '@/components/contacts/ContactsList';
import { AddContactModal } from '@/components/contacts/AddContactModal';
import { EstablishmentsList } from '@/components/establishments/EstablishmentsList';
import { AccountingContactsList } from '@/components/accounting-contacts/AccountingContactsList';
import { InvoiceChangeReview } from '@/components/invoices/InvoiceChangeReview';
import { ProjectPayerChoice, type CarriedClientDetails } from '@/components/invoices/ProjectPayerChoice';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { OrganizationFormData, OrganizationUpdate } from '@/lib/schemas';
import type { InvoiceChangeIntent } from '@/lib/invoiceSelectiveHistoryContracts';

export function OrganizationEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  // 9.12a: Inline contact editing state
  const [editingContactId, setEditingContactId] = useState<string | null>(null);

  const { data: organizationData, isLoading } = useOrganization(id);
  const updateOrganization = useUpdateOrganization();
  const deleteOrganization = useDeleteOrganization();

  // Contact handlers
  // R1 AC17: the Contacts list shows general people only. People added as default contacts live in the
  // "Default contacts" list below, so nobody appears twice (once with their email, once without). The
  // filter is also applied here, so a server that still sends every row cannot bring the duplicate back.
  const { data: contactsData } = useOrganizationContacts(id, { generalOnly: true });
  const updateContact = useUpdateOrganizationContact();
  const deleteContact = useDeleteOrganizationContact();
  const contacts = (contactsData?.contacts || []).filter((c: { is_accounting_contact?: boolean | null }) => !c.is_accounting_contact);

  // Establishment handlers (Phase 9.18d)
  const { data: establishmentsData } = useOrganizationEstablishments(id);
  const createEstablishment = useCreateOrganizationEstablishment();
  const deleteEstablishment = useDeleteOrganizationEstablishment();
  const establishments = establishmentsData?.establishments || [];
  const [pendingOrganizationReview, setPendingOrganizationReview] = useState<{
    intent: InvoiceChangeIntent;
    ordinaryPayload: OrganizationUpdate;
  } | null>(null);
  const [postReviewSaveError, setPostReviewSaveError] = useState(false);
  // R2 AC2: a name change first asks what she means. Nothing is written until she answers.
  const [intentQuestion, setIntentQuestion] = useState<{
    fields: Partial<Record<'legal_name' | 'display_name' | 'billing_address' | 'address', string>>;
    ordinaryPayload: OrganizationUpdate;
    carried: CarriedClientDetails;
  } | null>(null);
  const [questionOpen, setQuestionOpen] = useState(false);
  // R2 AC4: "A project is moving to a different client": the project flow for this client, then its review.
  const [movingProject, setMovingProject] = useState<{ carried: CarriedClientDetails } | null>(null);
  const [moveReview, setMoveReview] = useState<{ intent: InvoiceChangeIntent; projectIds: string[] } | null>(null);
  const [moveDone, setMoveDone] = useState(false);
  const [projectCount, setProjectCount] = useState<number | null>(null);

  // How many projects use this client, for the sentence under "Same company with a new name".
  useEffect(() => {
    if (!intentQuestion || !questionOpen || !id) return;
    let live = true;
    api.projects.list({ client_organization_id: id, limit: 1, include_archived: true, is_template: false })
      .then((result) => { if (live) setProjectCount(result.total); })
      .catch(() => { if (live) setProjectCount(null); });
    return () => { live = false; };
  }, [intentQuestion, questionOpen, id]);

  const commitOrgUpdate = async (payload: OrganizationUpdate): Promise<boolean> => {
    if (!id) return false;
    try {
      if (Object.keys(payload).length === 0) {
        setPendingOrganizationReview(null);
        navigate(-1);
        return true;
      }
      await updateOrganization.mutateAsync({ id, data: payload });
      setPendingOrganizationReview(null);
      // Phase 9.14: Use navigate(-1) to return to previous page with pagination preserved
      navigate(-1);
      return true;
    } catch (error) {
      console.error('Failed to update organization:', error);
      return false;
    }
  };

  const handleSubmit = async (data: OrganizationFormData) => {
    if (!id) return;

    const payload: OrganizationUpdate = {
      ...data,
      legal_name: data.legal_name || null,
      email: data.email || null,
      phone: data.phone || null,
      address: data.address || null,
      billing_address: data.billing_address || null,
      notes: data.notes || null,
      payment_terms: data.payment_terms ? parseInt(data.payment_terms) : 30,
      // Phase 5 Batch 6 Phase 4: Use role flags from form data
      is_client: data.is_client,
      is_consultant: data.is_consultant,
      is_government: data.is_government,
      // Phase 6 Batch 2: Organization category (optional)
      category_id: data.category_id || null,
    };

    const current = organizationData?.organization;
    const fields: Partial<Record<'legal_name' | 'display_name' | 'billing_address' | 'address', string>> = {};
    const nextName = data.name.trim();
    if (nextName !== (current?.name ?? '')) fields.display_name = nextName;
    const nextLegalName = data.legal_name?.trim() || '';
    if (nextLegalName !== (current?.legal_name ?? '')) fields.legal_name = nextLegalName;
    const nextAddress = data.address?.trim() || '';
    if (nextAddress !== (current?.address ?? '')) fields.address = nextAddress;
    const nextBillingAddress = data.billing_address?.trim() || '';
    if (nextBillingAddress !== (current?.billing_address ?? '')) fields.billing_address = nextBillingAddress;

    const ordinaryPayload = { ...payload };
    delete ordinaryPayload.name;
    delete ordinaryPayload.legal_name;
    delete ordinaryPayload.address;
    delete ordinaryPayload.billing_address;
    if (fields.display_name !== undefined || fields.legal_name !== undefined) {
      // R2 AC2: ask first. Renaming the shared client is only one of the two things she might mean.
      setPostReviewSaveError(false);
      setMoveDone(false);
      setProjectCount(null);
      setIntentQuestion({
        fields, ordinaryPayload,
        carried: { displayName: nextName, legalName: nextLegalName, billingAddress: nextBillingAddress },
      });
      setQuestionOpen(true);
      return;
    }
    if (Object.keys(fields).length > 0) {
      // An address change keeps today's path: the shared-correction review, which says how many projects it reaches.
      setPostReviewSaveError(false);
      setPendingOrganizationReview({
        intent: { intent: 'shared_correction', organization_id: id, fields },
        ordinaryPayload,
      });
      return;
    }
    await commitOrgUpdate(ordinaryPayload);
  };

  const chooseSameCompany = () => {
    if (!intentQuestion || !id) return;
    setPendingOrganizationReview({
      intent: { intent: 'shared_correction', organization_id: id, fields: intentQuestion.fields },
      ordinaryPayload: intentQuestion.ordinaryPayload,
    });
    setQuestionOpen(false);
    setIntentQuestion(null);
  };

  const chooseProjectMoving = () => {
    if (!intentQuestion) return;
    // Nothing about the client record is written. What she typed is offered again if she creates the new client.
    setMovingProject({ carried: intentQuestion.carried });
    setQuestionOpen(false);
  };

  const cancelQuestion = () => {
    setQuestionOpen(false);
    setIntentQuestion(null);
  };

  const handleOrganizationReviewApplied = async () => {
    if (!pendingOrganizationReview) return;
    const ordinaryPayload = pendingOrganizationReview.ordinaryPayload;
    setPendingOrganizationReview(null);
    const saved = await commitOrgUpdate(ordinaryPayload);
    if (!saved) setPostReviewSaveError(true);
  };

  const handleDelete = () => {
    console.log('🗑️ handleDelete called, id:', id);
    if (!id) {
      console.error('❌ No ID - cannot delete');
      return;
    }

    console.log('🔄 Starting delete mutation for organization:', id);
    deleteOrganization.mutate(id, {
      onSuccess: () => {
        console.log('✅ Delete successful');
        setShowDeleteDialog(false);
        // Phase 9.14: Use navigate(-1) to return to previous page with pagination preserved
        navigate(-1);
      },
      onError: (error) => {
        console.error('❌ Delete failed:', error);
        setShowDeleteDialog(false);
      },
    });
  };

  const handleSetPrimary = (contactId: string) => {
    if (!id) return;
    updateContact.mutate({
      organizationId: id,
      contactId,
      data: { is_primary_contact: true },
    });
  };

  const handleDeleteContact = (contactId: string) => {
    if (!id) return;
    deleteContact.mutate({ organizationId: id, contactId });
  };

  // 9.12a: Handle inline contact edit save
  const handleSaveEdit = (contactId: string, data: ContactEditData) => {
    if (!id) return;
    updateContact.mutate({
      organizationId: id,
      contactId,
      data: {
        name: data.name,
        email: data.email || undefined,
        phone: data.phone || undefined,
        title: data.title || undefined,
        // v0.14.5 R2: forward the inline primary toggle. Sent explicitly
        // (even when false) so demote-to-zero works — the backend treats
        // is_primary_contact=false as a real demote, true as an atomic
        // promote that clears other primaries.
        is_primary_contact: data.is_primary,
      },
    }, {
      onSuccess: () => {
        setEditingContactId(null);
      },
    });
  };

  // 9.18d: Establishment handlers
  // v0.12.26: address added so users can set establishment-level billing address inline
  const handleAddEstablishment = (data: { name: string; legal_name?: string | null; is_default: boolean; address?: string | null }) => {
    if (!id) return;
    createEstablishment.mutate({ organizationId: id, ...data });
  };

  const handleDeleteEstablishment = (establishmentId: number) => {
    if (!id) return;
    deleteEstablishment.mutate({ organizationId: id, establishmentId });
  };

  if (isLoading) {
    return (
      <DashboardLayout title="Edit Client">
        <div className="max-w-2xl space-y-6">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      </DashboardLayout>
    );
  }

  if (!organizationData?.organization) {
    return (
      <DashboardLayout title="Edit Client">
        <div className="max-w-2xl">
          <p className="text-destructive">Client not found</p>
          <Button onClick={() => navigate('/organizations')} className="mt-4">
            Back to Contacts
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <>
      <DashboardLayout title={`Edit ${organizationData.organization.name}`}>
        {/* Two-column grid: form | contacts */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Left column: Organization details */}
          <div className="space-y-6">
            <OrganizationForm
              key={organizationData.organization.id} // Force reinit when org changes
              mode="edit"
              initialData={organizationData.organization}
              onSubmit={handleSubmit}
              onCancel={() => navigate(-1)}  // Phase 9.14: Preserve pagination
            />

            {pendingOrganizationReview && (
              <p className="text-sm text-slate-600" data-testid="org-shared-correction-context">
                This changes the client's details everywhere they are used. Choose below which existing invoices
                change too. To bill one project to a different client instead, cancel and choose
                "A project is moving to a different client".
              </p>
            )}
            {moveDone && (
              <p className="text-sm text-slate-700" role="status" data-testid="org-move-done">
                The project was moved. The edits on this page were not saved.
              </p>
            )}
            {postReviewSaveError && (
              <p className="text-sm text-destructive" role="alert" data-testid="org-post-review-save-error">
                The reviewed billing correction was applied, but the other client details did not save. Please save those details again.
              </p>
            )}

            {updateOrganization.isError && (
              <p className="text-sm text-destructive">
                {updateOrganization.error?.message || 'Failed to update client'}
              </p>
            )}
          </div>

          {/* Right column: Contacts & Establishments */}
          <div className="space-y-6">
            <ContactsList
              contacts={contacts}
              onAddContact={() => setShowAddContactModal(true)}
              onDeleteContact={handleDeleteContact}
              onSetPrimary={handleSetPrimary}
              // 9.12a: Inline editing props
              editingContactId={editingContactId}
              onStartEdit={setEditingContactId}
              onSaveEdit={handleSaveEdit}
              onCancelEdit={() => setEditingContactId(null)}
            />

            {/* 9.18d: Establishments section */}
            <EstablishmentsList
              establishments={establishments}
              onAddEstablishment={handleAddEstablishment}
              onDeleteEstablishment={handleDeleteEstablishment}
              isAddingEstablishment={createEstablishment.isPending}
              organizationLegalName={organizationData.organization.legal_name}
              organizationId={id || ''}
            />

            {/* Client-level accounting contacts — fallback for DBAs without their own */}
            <AccountingContactsList
              orgId={id || ''}
              title="Default contacts (used when a DBA has none)"
              testId="org-accounting-contacts"
            />
          </div>
        </div>

        {/* Delete Button (full width, below grid) */}
        <div className="mt-8 flex justify-end">
          <Button
            type="button"
            variant="destructive"
            onClick={() => setShowDeleteDialog(true)}
          >
            Delete Client
          </Button>
        </div>
      </DashboardLayout>

      {/* Add Contact Modal */}
      <AddContactModal
        open={showAddContactModal}
        onOpenChange={setShowAddContactModal}
        organizationId={id || ''}
        onContactAdded={() => setShowAddContactModal(false)}
      />

      {pendingOrganizationReview && (
        <InvoiceChangeReview
          intent={pendingOrganizationReview.intent}
          onClose={() => setPendingOrganizationReview(null)}
          onApplied={handleOrganizationReviewApplied}
        />
      )}

      {/* R2 AC2: what did she mean by changing the name? */}
      <Dialog open={questionOpen && !!intentQuestion} onOpenChange={(open) => { if (!open) cancelQuestion(); }}>
        <DialogContent className="max-w-lg" data-testid="org-intent-question">
          <DialogHeader>
            <DialogTitle>What changed?</DialogTitle>
            <DialogDescription>Pick the one that matches what you are doing.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Button type="button" variant="outline" className="w-full justify-start" onClick={chooseSameCompany}>
                Same company with a new name
              </Button>
              <p className="text-sm text-slate-600" data-testid="same-company-sentence">
                {`The name changes everywhere ${organizationData.organization.name} is used${projectCount === null ? '' : `, including ${projectCount} ${projectCount === 1 ? 'project' : 'projects'}`}.`}
              </p>
            </div>
            <div className="space-y-1">
              <Button type="button" variant="outline" className="w-full justify-start" onClick={chooseProjectMoving}>
                A project is moving to a different client
              </Button>
              <p className="text-sm text-slate-600" data-testid="moving-project-sentence">
                Your edits here are not saved. You will pick the project and the new client next.
              </p>
            </div>
          </div>
          <div className="flex justify-end">
            <Button type="button" variant="outline" onClick={cancelQuestion}>Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* R2 AC4: the project flow for this client, with no project chosen yet. */}
      <Dialog open={!!movingProject && !moveReview}
        onOpenChange={(open) => { if (!open) { setMovingProject(null); setIntentQuestion(null); } }}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="org-move-project-flow">
          <DialogHeader>
            <DialogTitle>Move a project to a different client</DialogTitle>
            <DialogDescription>Nothing on this client is changed.</DialogDescription>
          </DialogHeader>
          {movingProject && id && (
            <ProjectPayerChoice
              organizationId={id}
              carried={movingProject.carried}
              onBack={() => {
                // The same question again, with the same edits: nothing was saved or lost.
                setMovingProject(null);
                setQuestionOpen(true);
              }}
              onCancel={() => { setMovingProject(null); setIntentQuestion(null); }}
              onIntent={(intent, projectIds) => setMoveReview({ intent, projectIds })}
            />
          )}
        </DialogContent>
      </Dialog>
      {moveReview && (
        <InvoiceChangeReview
          intent={moveReview.intent}
          focusedProjectIds={moveReview.projectIds}
          onClose={() => setMoveReview(null)}
          onApplied={() => { setMoveReview(null); setMovingProject(null); setIntentQuestion(null); setMoveDone(true); }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Client</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{organizationData?.organization?.name}"? This action cannot be undone.
              {organizationData?.organization && (
                <span className="block mt-2 text-orange-600">
                  Note: This client cannot be deleted if they have associated projects.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteOrganization.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
